import {
  Grid,
  Typography,
  FormControl,
  FormHelperText,
  Autocomplete,
  Box,
  LinearProgress,
  Button,
  Chip,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  useGetSessionsPaginatedQuery,
  useStartSessionTransferMutation,
  useGetTransferStatusQuery,
  useCancelTransferMutation,
  useGetTransferHistoryQuery,
} from "../settings/session/sessionApiSlice";
import {
  useGetClassesPaginatedQuery,
  useGetAllClassroomsQuery,
  useGetClassesQuery,
} from "../classroom/classApiSlice";
import toast from "../../../helpers/toast";

// Sentinel value used to represent the "Graduate" destination option
const GRADUATE_OPTION = { id: "graduate", name: "Graduate (Finish School)" };
const DEFAULT_CLASS_OPTION = { id: "default", name: "Use Class Default Destination" };
const KEEP_SAME_CLASS_OPTION = { id: "keep_same", name: "Keep In Same Class" };

const validationSchema = yup.object({
  from_session: yup.object().required("Source session is required"),
  to_session: yup.object().required("Target session is required"),
});

const statusColor = (status) => {
  switch (status) {
    case "complete": return "success";
    case "pending": return "warning";
    case "failed": return "error";
    case "cancelled": return "default";
    default: return "default";
  }
};

export default function SessionTransfer() {
  const [transferTaskId, setTransferTaskId] = useState(null);
  const [transferDone, setTransferDone] = useState(false);
  const [transferProgress, setTransferProgress] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);

  const [startTransfer, { isLoading: isStarting }] = useStartSessionTransferMutation();
  const [cancelTransfer, { isLoading: isCancelling }] = useCancelTransferMutation();

  const { data: historyData, refetch: refetchHistory } = useGetTransferHistoryQuery(
    { page: historyPage, per_page: 10 },
  );

  const isPolling = transferTaskId && !transferDone;
  const { data: statusData } = useGetTransferStatusQuery(transferTaskId, {
    skip: !transferTaskId || transferDone,
    pollingInterval: isPolling ? 3000 : 0,
  });

  useEffect(() => {
    if (!statusData) return;
    const { state, info } = statusData;
    if (state === "PROGRESS" && info) {
      setTransferProgress(info);
    } else if (state === "SUCCESS") {
      setTransferDone(true);
      setTransferProgress(null);
      setTransferTaskId(null);
      toast.success("Student transfer completed successfully");
      refetchHistory();
    } else if (state === "FAILURE") {
      setTransferDone(true);
      setTransferProgress(null);
      setTransferTaskId(null);
      const failMsg = typeof info === "string" ? info : (info?.exc_message || "Transfer failed");
      toast.error(failMsg);
      refetchHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData]);

  const handleCancel = async () => {
    if (!transferTaskId) return;
    try {
      await cancelTransfer(transferTaskId).unwrap();
      setTransferTaskId(null);
      setTransferDone(true);
      setTransferProgress(null);
      toast.info("Transfer cancelled.");
      refetchHistory();
    } catch (error) {
      const msg = error?.data?.error || error?.data?.message || error?.message || "Failed to cancel transfer";
      toast.error(msg);
    }
  };

  // Session search
  const [fromSessionSearch, setFromSessionSearch] = useState("");
  const [toSessionSearch, setToSessionSearch] = useState("");
  const { data: fromSessionsData } = useGetSessionsPaginatedQuery({ page: 1, per_page: 10, q: fromSessionSearch });
  const { data: toSessionsData } = useGetSessionsPaginatedQuery({ page: 1, per_page: 10, q: toSessionSearch });
  const fromSessions = fromSessionsData?.sessions ?? [];
  const toSessions = toSessionsData?.sessions ?? [];

  // Classroom search (only needed when scope = 'class')
  const [scope, setScope] = useState("promotions");
  const [classroomSearch, setClassroomSearch] = useState("");
  const { data: classroomsData } = useGetClassesPaginatedQuery(
    { page: 1, per_page: 10, q: classroomSearch },
    { skip: scope !== "class" },
  );
  const classrooms = classroomsData?.classrooms ?? [];

  // All classrooms (needed for promotions scope)
  const { data: allClassroomsData, isLoading: isLoadingAllClassrooms } = useGetAllClassroomsQuery(
    undefined,
    { skip: scope !== "promotions" },
  );
  const allClassrooms = useMemo(() => allClassroomsData ?? [], [allClassroomsData]);

  // Destination options for the promotions mapping: all classrooms + Graduate sentinel
  const destinationOptions = useMemo(
    () => [GRADUATE_OPTION, ...allClassrooms],
    [allClassrooms]
  );

  // promotionMappings: { [from_classroom_id]: { destination: classroomObj | GRADUATE_OPTION | null } }
  const [promotionMappings, setPromotionMappings] = useState({});
  // studentMappings shape: { [from_classroom_id]: { [student_id]: optionObject } }
  const [studentMappings, setStudentMappings] = useState({});
  const [overrideDialogClassroom, setOverrideDialogClassroom] = useState(null);

  const isOverrideDialogOpen = Boolean(overrideDialogClassroom);

  // Re-initialise mapping keys whenever the classroom list loads
  useEffect(() => {
    if (scope !== "promotions" || allClassrooms.length === 0) return;
    setPromotionMappings((prev) => {
      const next = {};
      allClassrooms.forEach((cls) => {
        next[cls.id] = prev[cls.id] ?? { destination: null, is_excluded: false };
      });
      return next;
    });
  }, [allClassrooms, scope]);

  const unmappedCount = useMemo(() => {
    if (scope !== "promotions") return 0;
    return allClassrooms.filter(
      (cls) => !promotionMappings[cls.id]?.is_excluded && !promotionMappings[cls.id]?.destination
    ).length;
  }, [allClassrooms, promotionMappings, scope]);

  const formik = useFormik({
    initialValues: {
      from_session: null,
      to_session: null,
      classroom: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (scope === "class" && !values.classroom) {
        toast.error("Please select a classroom");
        return;
      }
      if (scope === "promotions" && unmappedCount > 0) {
        toast.error(`${unmappedCount} class${unmappedCount !== 1 ? "es" : ""} still need a destination`);
        return;
      }
      try {
        let body = {
          from_session_id: values.from_session.id,
          to_session_id: values.to_session.id,
          type: scope === "class" ? "specific_class" : scope === "promotions" ? "promotions" : "whole_school",
        };

        if (scope === "class") {
          body.classroom_id = values.classroom?.id;
        } else if (scope === "promotions") {
          body.mappings = allClassrooms
            .filter((cls) => !promotionMappings[cls.id]?.is_excluded)
            .map((cls) => {
              const dest = promotionMappings[cls.id]?.destination;
              return {
                from_classroom_id: cls.id,
                to_classroom_id: dest?.id === "graduate" ? null : dest?.id ?? null,
                is_graduation: dest?.id === "graduate",
              };
            });

          const studentOverrideRows = [];
          Object.entries(studentMappings).forEach(([fromClassroomIdRaw, byStudent]) => {
            const fromClassroomId = Number(fromClassroomIdRaw);
            Object.entries(byStudent || {}).forEach(([studentIdRaw, destination]) => {
              if (!destination || destination.id === "default") return;
              const studentId = Number(studentIdRaw);
              let toClassroomId = null;
              let isGraduation = false;

              if (destination.id === "graduate") {
                isGraduation = true;
              } else if (destination.id === "keep_same") {
                toClassroomId = fromClassroomId;
              } else {
                toClassroomId = destination.id;
              }

              studentOverrideRows.push({
                from_classroom_id: fromClassroomId,
                student_id: studentId,
                to_classroom_id: toClassroomId,
                is_graduation: isGraduation,
              });
            });
          });

          if (studentOverrideRows.length > 0) {
            body.student_mappings = studentOverrideRows;
          }
        }

        const response = await startTransfer(body).unwrap();
        setTransferDone(false);
        setTransferTaskId(response.task_id);
        toast.info(response.message || "Transfer started");
      } catch (error) {
        const msg = error?.data?.error || error?.data?.message || error?.message || "Failed to start transfer";
        toast.error(msg);
      }
    },
  });

  const progressPercent = transferProgress
    ? transferProgress.total_classrooms > 0
      ? Math.round(
          ((transferProgress.classroom_index - 1) / transferProgress.total_classrooms +
            (transferProgress.student_index || 0) /
              (transferProgress.total_students_in_class || 1) /
              transferProgress.total_classrooms) *
            100
        )
      : 0
    : 0;

  const history = historyData?.transfers ?? [];
  const pagination = historyData?.pagination;

  // Reset overrides when source session changes to avoid mixing students across sessions.
  useEffect(() => {
    setStudentMappings({});
    setOverrideDialogClassroom(null);
  }, [formik.values.from_session?.id]);

  const selectedClassroomId = overrideDialogClassroom?.id;
  const selectedFromSessionId = formik.values.from_session?.id;
  const shouldFetchOverrideStudents = Boolean(isOverrideDialogOpen && selectedClassroomId && selectedFromSessionId);
  const { data: overrideStudentsData, isLoading: isLoadingOverrideStudents } = useGetClassesQuery(
    shouldFetchOverrideStudents
      ? `${selectedClassroomId}/students?session_id=${selectedFromSessionId}`
      : "",
    { skip: !shouldFetchOverrideStudents }
  );
  const overrideStudents = Array.isArray(overrideStudentsData) ? overrideStudentsData : [];

  const getOverrideCountForClassroom = (classroomId) => {
    const entries = Object.values(studentMappings[classroomId] || {});
    return entries.filter((dest) => dest && dest.id !== "default").length;
  };

  const studentDestinationOptions = useMemo(() => {
    return [DEFAULT_CLASS_OPTION, KEEP_SAME_CLASS_OPTION, GRADUATE_OPTION, ...allClassrooms];
  }, [allClassrooms]);

  return (
    <div style={{ padding: "0 10px" }}>
      <Grid container columnSpacing={4} rowSpacing={3}>
        {/* ── Form ─────────────────────────────────────────── */}
        <Grid item xs={12} md={7}>
          <StyledPaper sx={{ padding: "15px" }}>
            <Typography variant="formHeading" sx={{ marginBottom: "20px" }}>
              Transfer Students
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Copy student-class enrollment from one session to another. Students already
              enrolled in the target session are skipped automatically.
            </Typography>

            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container rowSpacing={3}>
                {/* From Session */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    error={formik.touched.from_session && Boolean(formik.errors.from_session)}
                  >
                    <Typography variant="formSubHeading">FROM SESSION</Typography>
                    <Autocomplete
                      options={fromSessions}
                      getOptionLabel={(o) => o.name || ""}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      value={formik.values.from_session || null}
                      onChange={(_, value) => formik.setFieldValue("from_session", value)}
                      onInputChange={(_, value, reason) => {
                        if (reason === "input") setFromSessionSearch(value);
                      }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search session..."
                          error={formik.touched.from_session && Boolean(formik.errors.from_session)}
                        />
                      )}
                    />
                    <FormHelperText>
                      {formik.touched.from_session && formik.errors.from_session}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                {/* To Session */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    error={formik.touched.to_session && Boolean(formik.errors.to_session)}
                  >
                    <Typography variant="formSubHeading">TO SESSION</Typography>
                    <Autocomplete
                      options={toSessions}
                      getOptionLabel={(o) => o.name || ""}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      value={formik.values.to_session || null}
                      onChange={(_, value) => formik.setFieldValue("to_session", value)}
                      onInputChange={(_, value, reason) => {
                        if (reason === "input") setToSessionSearch(value);
                      }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search session..."
                          error={formik.touched.to_session && Boolean(formik.errors.to_session)}
                        />
                      )}
                    />
                    <FormHelperText>
                      {formik.touched.to_session && formik.errors.to_session}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                {/* Scope — hidden, promotions always active */}

                {/* Classroom (conditional — specific class) */}
                {scope === "class" && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <Typography variant="formSubHeading">CLASSROOM</Typography>
                      <Autocomplete
                        options={classrooms}
                        getOptionLabel={(o) => o.name || ""}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={formik.values.classroom || null}
                        onChange={(_, value) => formik.setFieldValue("classroom", value)}
                        onInputChange={(_, value, reason) => {
                          if (reason === "input") setClassroomSearch(value);
                        }}
                        filterOptions={(x) => x}
                        renderInput={(params) => (
                          <StyledTextField {...params} placeholder="Search classroom..." />
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}

                {/* Promotions Mapping Table (inline, above submit) */}
                {scope === "promotions" && (
                  <Grid item xs={12}>
                    <Typography variant="formSubHeading" sx={{ mb: 1 }}>CLASS PROMOTION MAPPING</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Assign each class a destination in the new session, or select "Graduate" to graduate all students from that class.
                    </Typography>
                    {isLoadingAllClassrooms ? (
                      <LinearProgress />
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <StyledTableCell padding="checkbox">Include</StyledTableCell>
                              <StyledTableCell>Current Class</StyledTableCell>
                              <StyledTableCell>Destination</StyledTableCell>
                              <StyledTableCell>Student Overrides</StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {allClassrooms.length === 0 ? (
                              <StyledTableRow>
                                <StyledTableCell colSpan={4} align="center">No classrooms found</StyledTableCell>
                              </StyledTableRow>
                            ) : (
                              allClassrooms.map((cls) => {
                                const isExcluded = promotionMappings[cls.id]?.is_excluded ?? false;
                                return (
                                  <StyledTableRow
                                    key={cls.id}
                                    sx={{ opacity: isExcluded ? 0.45 : 1, transition: "opacity 0.2s" }}
                                  >
                                    <StyledTableCell padding="checkbox">
                                      <Checkbox
                                        size="small"
                                        checked={!isExcluded}
                                        onChange={(e) =>
                                          setPromotionMappings((prev) => ({
                                            ...prev,
                                            [cls.id]: {
                                              ...prev[cls.id],
                                              is_excluded: !e.target.checked,
                                            },
                                          }))
                                        }
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell>{cls.name}</StyledTableCell>
                                    <StyledTableCell>
                                      <Autocomplete
                                        size="small"
                                        disabled={isExcluded}
                                        options={destinationOptions.filter((o) => o.id !== cls.id)}
                                        getOptionLabel={(o) => o.name || ""}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        value={promotionMappings[cls.id]?.destination || null}
                                        onChange={(_, value) =>
                                          setPromotionMappings((prev) => ({
                                            ...prev,
                                            [cls.id]: { ...prev[cls.id], destination: value },
                                          }))
                                        }
                                        renderInput={(params) => (
                                          <StyledTextField
                                            {...params}
                                            placeholder="Select destination..."
                                            size="small"
                                          />
                                        )}
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={isExcluded}
                                        onClick={() => {
                                          if (!formik.values.from_session) {
                                            toast.error("Please select a source session first");
                                            return;
                                          }
                                          setOverrideDialogClassroom(cls);
                                        }}
                                        sx={{ textTransform: "none" }}
                                      >
                                        Configure ({getOverrideCountForClassroom(cls.id)})
                                      </Button>
                                    </StyledTableCell>
                                  </StyledTableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Grid>
                )}

                {/* Submit */}
                <Grid item xs={12}>
                  {scope === "promotions" && unmappedCount > 0 && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      {unmappedCount} class{unmappedCount !== 1 ? "es" : ""} still need a destination
                    </Typography>
                  )}
                  <Box textAlign="right">
                    <StyledLoadingButton
                      loading={isStarting || isPolling}
                      disabled={
                        isPolling ||
                        !formik.values.from_session ||
                        !formik.values.to_session ||
                        (scope === "promotions" && (unmappedCount > 0 || isLoadingAllClassrooms))
                      }
                      type="submit"
                      sx={{ textTransform: "none" }}
                    >
                      {isPolling ? "Transferring..." : "Start Transfer"}
                    </StyledLoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </StyledPaper>
        </Grid>

        {/* ── Progress + History ───────────────────────────── */}
        <Grid item xs={12} md={5}>
          {/* Progress card */}
          {isPolling && transferProgress && (
            <StyledPaper sx={{ padding: "15px", mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="formHeading">Transfer In Progress</Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={isCancelling}
                  onClick={handleCancel}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Class {transferProgress.classroom_index} of {transferProgress.total_classrooms}
                {transferProgress.current_classroom ? ` — ${transferProgress.current_classroom}` : ""}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {transferProgress.status}
              </Typography>
              <LinearProgress variant="determinate" value={progressPercent} sx={{ borderRadius: 1, height: 8 }} />
            </StyledPaper>
          )}

          {/* History table */}
          <StyledPaper sx={{ paddingBottom: "25px" }}>
            <Box sx={{ padding: "15px" }}>
              <Typography variant="formHeading">Transfer History</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>From</StyledTableCell>
                    <StyledTableCell>To</StyledTableCell>
                    <StyledTableCell>Scope</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Date</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={5} align="center">
                        No transfers yet
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    history.map((t) => (
                      <StyledTableRow key={t.id}>
                        <StyledTableCell>{t.from_session?.name ?? t.from_session_id}</StyledTableCell>
                        <StyledTableCell>{t.to_session?.name ?? t.to_session_id}</StyledTableCell>
                        <StyledTableCell>
                          {t.type === "promotions"
                            ? "Promotions"
                            : t.classroom
                            ? t.classroom.name
                            : "Whole School"}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Chip label={t.status} color={statusColor(t.status)} size="small" />
                        </StyledTableCell>
                        <StyledTableCell>
                          {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {pagination && pagination.total_pages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, padding: "10px 15px 0" }}>
                <Button
                  size="small"
                  disabled={historyPage <= 1}
                  onClick={() => setHistoryPage((p) => p - 1)}
                >
                  Prev
                </Button>
                <Typography variant="body2" sx={{ alignSelf: "center" }}>
                  {historyPage} / {pagination.total_pages}
                </Typography>
                <Button
                  size="small"
                  disabled={historyPage >= pagination.total_pages}
                  onClick={() => setHistoryPage((p) => p + 1)}
                >
                  Next
                </Button>
              </Box>
            )}
          </StyledPaper>
        </Grid>
      </Grid>

      <Dialog
        open={isOverrideDialogOpen}
        onClose={() => setOverrideDialogClassroom(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Student Overrides {overrideDialogClassroom ? `- ${overrideDialogClassroom.name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            For selected students, choose a custom destination. Students left on
            "Use Class Default Destination" will follow the class-level mapping.
          </Typography>
          {isLoadingOverrideStudents ? (
            <LinearProgress />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Student</StyledTableCell>
                    <StyledTableCell>Reg Number</StyledTableCell>
                    <StyledTableCell>Destination Override</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overrideStudents.length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={3} align="center">
                        No students found in this class for the selected source session
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    overrideStudents.map((student) => {
                      const classroomId = overrideDialogClassroom?.id;
                      const selectedDestination =
                        studentMappings[classroomId]?.[student.id] || DEFAULT_CLASS_OPTION;

                      return (
                        <StyledTableRow key={student.id}>
                          <StyledTableCell>{`${student.first_name || ""} ${student.last_name || ""}`.trim()}</StyledTableCell>
                          <StyledTableCell>{student.reg_number || "-"}</StyledTableCell>
                          <StyledTableCell>
                            <Autocomplete
                              size="small"
                              options={studentDestinationOptions.filter((option) => {
                                // Prevent selecting the same class as a custom destination; use "Keep In Same Class" instead.
                                if (option.id === overrideDialogClassroom?.id) return false;
                                return true;
                              })}
                              getOptionLabel={(o) => o.name || ""}
                              isOptionEqualToValue={(o, v) => o.id === v.id}
                              value={selectedDestination}
                              onChange={(_, value) => {
                                setStudentMappings((prev) => ({
                                  ...prev,
                                  [classroomId]: {
                                    ...(prev[classroomId] || {}),
                                    [student.id]: value || DEFAULT_CLASS_OPTION,
                                  },
                                }));
                              }}
                              renderInput={(params) => (
                                <StyledTextField
                                  {...params}
                                  placeholder="Use class default"
                                  size="small"
                                />
                              )}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogClassroom(null)} sx={{ textTransform: "none" }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
